import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import EditDetailsModal, { Field } from '../../components/common/EditDetailsModal';
import DualDatePicker from '../../components/common/DualDatePicker';
import DualDate from '../../components/common/DualDate';
import { getGradingPreview, updateStudent } from '../../services/studentService';
import { BELT_ORDER } from '../../constants/belts';
import { ktmToday } from '../../utils/calendar';
import { BELT_MAP } from '../../components/common/BeltBadge';

// Shared between the ladder index (/admin/grading) and the per-belt list
// (/admin/grading/:belt) — kept in one place so the two pages can't drift apart on what a
// "close" count or a "distance to go" actually means.

// The stripe/accent colour for a belt (BELT_MAP's `border` field) — unlike `bg`, this is the
// value that actually differs between adjacent belts (Yellow and Yellow-Green Stripe share the
// same bg, for instance), which is what makes it the right one to use as structure here.
export const beltAccent = (belt) => BELT_MAP[belt]?.border || BELT_MAP.White.border;

export const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function joinNatural(items) {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// Eligibility needs BOTH requirements, so "how close" is the LIMITING ratio, not an average.
export function limitingRatio(s) {
  const months   = s.rule?.minMonths   > 0 ? Math.min(1, (s.monthsInGrade ?? 0) / s.rule.minMonths) : 1;
  const sessions = s.rule?.minSessions > 0 ? Math.min(1, (s.sessionsSinceGrading ?? 0) / s.rule.minSessions) : 1;
  return Math.min(months, sessions);
}

// The single binding distance — whichever requirement is actually behind, not both at once.
// Framed as "needs X more sessions/months", never a bare number — a bare "9 sessions" could be
// misread as a count of sessions already attended rather than the shortfall still owed.
export function distanceOf(s) {
  if (s.bucket === 'eligible') return { text: 'eligible', eligible: true };
  if (s.bucket === 'unknown')  return { text: 'no date on file', eligible: false };
  const monthsRatio   = s.rule?.minMonths   > 0 ? Math.min(1, (s.monthsInGrade ?? 0) / s.rule.minMonths) : 1;
  const sessionsRatio = s.rule?.minSessions  > 0 ? Math.min(1, (s.sessionsSinceGrading ?? 0) / s.rule.minSessions) : 1;
  const text = sessionsRatio <= monthsRatio
    ? `needs ${plural(s.sessionsShort, 'more session')}`
    : `needs ${plural(s.monthsShort, 'more month')}`;
  return { text, eligible: false };
}

export function StudentRow({ s, onCorrect }) {
  const d = distanceOf(s);
  return (
    <div className="py-2.5 border-b border-divider last:border-0 flex items-center justify-between gap-3">
      <span className="text-sm text-secondary truncate">{s.name}</span>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-xs font-medium" style={{ color: d.eligible ? beltAccent(s.currentBelt) : undefined }}>
          <span className={d.eligible ? '' : 'text-muted'}>{d.text}</span>
        </span>
        <button onClick={() => onCorrect(s)} className="text-xs font-medium text-primary hover:underline min-h-[44px] flex items-center">
          Correct belt
        </button>
      </div>
    </div>
  );
}

// Belt + date + (conditionally) reason. Live preview of what saving would mean for this
// student's eligibility — shown before anything is saved, since backdating a date is an
// eligibility decision someone could otherwise make without realizing it.
export function BeltCorrectionModal({ student, onClose, onSaved }) {
  const [belt, setBelt] = useState(student.currentBelt);
  const [date, setDate] = useState(ktmToday());
  const [reason, setReason] = useState('');
  const beltChanged = belt !== student.currentBelt;

  const { data: preview, isFetching: previewLoading } = useQuery({
    queryKey: ['grading-preview', student.studentId, belt, date],
    queryFn: () => getGradingPreview(student.studentId, { belt, achievedAt: date }).then(r => r.data),
    enabled: !!belt && !!date,
  });

  const mutation = useMutation({
    mutationFn: () => updateStudent(student.studentId, beltChanged
      ? { currentGrade: belt, beltChangeReason: reason, beltChangeDate: date }
      : { gradingBaselineDate: date }),
    onSuccess: () => { toast.success(`${student.name}'s belt record updated`); onSaved(); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to update'),
  });

  const canSave = beltChanged ? reason.trim().length > 0 : true;

  return (
    <EditDetailsModal
      title={beltChanged ? 'Correct belt' : 'Set grading baseline'}
      subtitle={`${student.name} — currently ${student.currentBelt}`}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) { toast.error('A reason is required when the belt is changing.'); return; }
        mutation.mutate();
      }}
      saving={mutation.isPending}
    >
      <Field label="Belt">
        <select
          value={belt}
          onChange={(e) => setBelt(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl text-sm bg-nav-hover/40 border border-divider text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[44px]"
        >
          {BELT_ORDER.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      </Field>

      <Field label={beltChanged ? 'Date earned' : 'Date earned (baseline)'} required>
        <DualDatePicker value={date} onChange={setDate} max={ktmToday()} fastNav />
      </Field>

      {beltChanged && (
        <Field label="Reason" required>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Transferred from Riverside Dojang, verified Green belt"
            rows={2}
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-nav-hover/40 border border-divider text-secondary placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </Field>
      )}

      <div className="rounded-xl bg-nav-hover/60 px-3.5 py-3 text-sm text-secondary">
        {previewLoading ? (
          <span className="text-muted">Checking…</span>
        ) : preview ? (
          <>
            Setting {belt} as of <DualDate date={date} adHidden /> means {student.name} will have{' '}
            {plural(preview.monthsInGrade, 'month')} in grade and {plural(preview.sessionsSinceGrading, 'session')}
            {preview.bucket === 'eligible'
              ? <> — <span className="font-medium" style={{ color: beltAccent(belt) }}>eligible</span>.</>
              : preview.monthsShort > 0 || preview.sessionsShort > 0
                ? <> — needs {[preview.monthsShort > 0 && plural(preview.monthsShort, 'more month'), preview.sessionsShort > 0 && plural(preview.sessionsShort, 'more session')].filter(Boolean).join(' and ')}.</>
                : '.'}
          </>
        ) : (
          <span className="text-muted">Pick a date to see the effect.</span>
        )}
      </div>
    </EditDetailsModal>
  );
}
